import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for comprehensive duel real-time subscriptions
 * Subscribes to duel status updates, proof uploads, verification completions, and activity feed
 */
export const useDuelRealtime = (duelId, userId, callbacks) => {
  const { onDuelUpdate, onProofUpload, onVerificationComplete, onCommentAdded, onReactionAdded } = callbacks || {};

  useEffect(() => {
    if (!duelId || !userId) return;

    // Subscribe to duel status and progress updates
    const duelChannel = supabase
      ?.channel(`duel_${duelId}_updates`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `id=eq.${duelId}`
        },
        (payload) => {
          onDuelUpdate?.(payload);
        }
      )
      ?.subscribe();

    // Subscribe to proof uploads for this duel
    const proofsChannel = supabase
      ?.channel(`duel_${duelId}_proofs`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duel_proofs',
          filter: `duel_id=eq.${duelId}`
        },
        (payload) => {
          onProofUpload?.(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'duel_proofs',
          filter: `duel_id=eq.${duelId}`
        },
        (payload) => {
          // Verification completion detected when auto_verified changes
          if (payload?.new?.auto_verified !== payload?.old?.auto_verified) {
            onVerificationComplete?.(payload);
          }
        }
      )
      ?.subscribe();

    // Subscribe to comments for activity feed
    const commentsChannel = supabase
      ?.channel(`duel_${duelId}_comments`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duel_comments',
          filter: `duel_id=eq.${duelId}`
        },
        (payload) => {
          onCommentAdded?.(payload);
        }
      )
      ?.subscribe();

    // Subscribe to reactions for activity feed
    const reactionsChannel = supabase
      ?.channel(`duel_${duelId}_reactions`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duel_reactions',
          filter: `duel_id=eq.${duelId}`
        },
        (payload) => {
          onReactionAdded?.(payload);
        }
      )
      ?.subscribe();

    // Cleanup subscriptions
    return () => {
      duelChannel?.unsubscribe();
      proofsChannel?.unsubscribe();
      commentsChannel?.unsubscribe();
      reactionsChannel?.unsubscribe();
    };
  }, [duelId, userId, onDuelUpdate, onProofUpload, onVerificationComplete, onCommentAdded, onReactionAdded]);
};

/**
 * Custom hook for user's all duels real-time updates
 * Subscribes to all duels where user is creator or opponent
 */
export const useUserDuelsRealtime = (userId, callbacks) => {
  const { onDuelUpdate, onProofUpload } = callbacks || {};

  useEffect(() => {
    if (!userId) return;

    // Subscribe to duel updates where user is creator or opponent
    const duelsChannel = supabase
      ?.channel('user_duels_realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `creator_id=eq.${userId}`
        },
        (payload) => {
          onDuelUpdate?.(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `opponent_id=eq.${userId}`
        },
        (payload) => {
          onDuelUpdate?.(payload);
        }
      )
      ?.subscribe();

    // Subscribe to proof uploads for user's duels
    const proofsChannel = supabase
      ?.channel('user_duel_proofs_realtime')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duel_proofs'
        },
        async (payload) => {
          // Check if this proof belongs to user's duel
          const { data: duel } = await supabase
            ?.from('duels')
            ?.select('id, creator_id, opponent_id')
            ?.eq('id', payload?.new?.duel_id)
            ?.single();

          if (duel && (duel?.creator_id === userId || duel?.opponent_id === userId)) {
            onProofUpload?.(payload);
          }
        }
      )
      ?.subscribe();

    // Cleanup subscriptions
    return () => {
      duelsChannel?.unsubscribe();
      proofsChannel?.unsubscribe();
    };
  }, [userId, onDuelUpdate, onProofUpload]);
};
